// Wishlist orchestration and the <wishlist-button> element.
//
// WRITES ARE LOCAL FIRST, ALWAYS. The heart flips from WishlistStore before the
// network is touched, and the push is not awaited. A failed push is SILENT to the
// shopper by design — the heart already reflects their intent, and a toast for
// something they cannot act on is noise — but never silent to us: it is logged and
// then reconciled by the next read.
//
// Reads are deliberately rare. GET /wishlist embeds the whole canonical product
// (~21KB an item), so running it per navigation would cost megabytes. It runs on the
// wishlist page, once per tab elsewhere, and as the fallback of a failed write.

// One reconcile per tab is the compromise that keeps ordinary navigation free while
// still picking up a change made in the app or on another device. sessionStorage,
// not localStorage: a new tab should re-check.
const WISHLIST_RECONCILED_KEY = "themex-wishlist-reconciled";

const WishlistSync = {
  // productId -> tail of that product's write chain. A fast double-tap fires add then
  // remove; unserialised, the remove runs before the add's row id exists, resolves
  // nothing to delete, and the add then creates the row — leaving the product
  // wishlisted against the shopper's last intent.
  _queues: new Map(),

  // Products with a write in flight. A read that started before them predates their
  // intent, so it may neither delete them nor re-add them.
  _pending: new Set(),

  // Re-seeds are memoised per identity so two overlapping reads do not both push the
  // whole list. Dropped again when anything failed to land, so the next read retries.
  _reseeds: new Map(),

  _refreshPromise: null,
  _foldPromise: null,

  _report(context, error) {
    console.warn(`[Wishlist] ${context}:`, error?.message ?? error);
  },

  _enqueue(productId, task) {
    const previous = this._queues.get(productId) ?? Promise.resolve();
    const next = previous.then(task, task);
    // The stored tail never rejects, so one failed write cannot break the chain for
    // every later toggle of the same product.
    const settled = next.catch(() => {});
    this._queues.set(productId, settled);
    // Drop the entry once nothing is queued behind it — otherwise the map keeps one
    // resolved promise per product ever hearted, for the life of the page.
    settled.then(() => {
      if (this._queues.get(productId) === settled) this._queues.delete(productId);
    });
    return next;
  },

  // Runs an operation against the current identity, recovering ONCE from a session
  // that died mid-flight (the 23h TTL can lapse between two requests). A second
  // failure fails the operation rather than recursing.
  //
  // Suppressed while a guest merge is pending: dropping that session would flip
  // identity to the customer, whose authoritative read would then prune the local
  // guest items, and the merge would become unretryable because it needs the session
  // it just discarded.
  // The operation receives `replaced` and `isRetry`, which are NOT the same thing and
  // must not be conflated. `replaced` is session-scoped and stays true for the rest of
  // the page — it says "this session started empty", which is what a read needs to know.
  // `isRetry` is true only on the second attempt, which is what a write needs: gating a
  // removal on `replaced` silently skips EVERY removal for the rest of the page once a
  // session has been minted.
  async _withSession(operation) {
    const { identity, replaced } = await WishlistIdentity.resolve();
    try {
      return await operation(identity, { replaced, isRetry: false });
    } catch (error) {
      if (!WishlistAPI.isSessionNotFound(error) || WishlistIdentity.hasPendingGuestMerge()) throw error;
      WishlistStore.clearSession();
      WishlistIdentity.reset();
      const retry = await WishlistIdentity.resolve();
      return operation(retry.identity, { replaced: retry.replaced, isRetry: true });
    }
  },

  // ---- writes ----

  add(productId) {
    return this._enqueue(productId, async () => {
      this._pending.add(productId);
      try {
        const itemId = await this._withSession((identity) => WishlistAPI.add(identity, productId));
        // Only if the shopper has not un-hearted it in the meantime — otherwise this
        // would write a row id onto an entry the remove already deleted, or resurrect it.
        if (WishlistStore.has(productId)) WishlistStore.mergeItemIds(productId, [itemId]);
      } catch (error) {
        this._report("add", error);
      } finally {
        this._pending.delete(productId);
      }
    });
  },

  // `itemIds` is a LIST: the API forks a row per (productId, variantId), removal
  // deletes by row id, and leaving one behind resurrects the product on the next read.
  // Every row is therefore attempted whatever any single one does.
  remove(productId, itemIds) {
    return this._enqueue(productId, async () => {
      if (!itemIds?.length) return;
      this._pending.add(productId);
      try {
        await this._withSession(async (identity, { isRetry }) => {
          // Only on the RETRY is there nothing to delete: the row ids it was handed
          // were learned from the session that just died, and the replacement is
          // empty. On the first attempt they are current and must be deleted.
          if (isRetry) return;
          const results = await Promise.allSettled(itemIds.map((itemId) => WishlistAPI.remove(identity, itemId)));
          const failure = results.find((result) => result.status === "rejected");
          // Rethrown AS-IS rather than aggregated: session recovery matches on the
          // error's own code, and wrapping would make a multi-row product the one
          // case it could never reach.
          if (failure) throw failure.reason;
        });
      } catch (error) {
        this._report("remove", error);
      } finally {
        this._pending.delete(productId);
      }
    });
  },

  // ---- re-seeding ----

  // Pushes every item the server has never acknowledged onto the given identity.
  // Parallel and uncapped: there is no bulk add endpoint and no rate limit, and
  // ordering does not matter because the store keeps its own addedAt.
  //
  // The two counts are reported separately because they have opposite lifetimes. A
  // push failure is transient and can land on a retry. An unkeyable id can never be
  // pushed to ANY identity, so blocking on it would block forever.
  reseed(identity) {
    const key = identity.customerId ? `c:${identity.customerId}` : `s:${identity.sessionId}`;
    if (this._reseeds.has(key)) return this._reseeds.get(key);

    const run = (async () => {
      const productIds = WishlistStore.unconfirmedProductIds();
      let pushFailures = 0;
      let unkeyable = 0;

      await Promise.all(
        productIds.map(async (productId) => {
          if (!WishlistAPI.productKey(productId)) {
            unkeyable += 1;
            return;
          }
          try {
            const itemId = await WishlistAPI.add(identity, productId);
            if (WishlistStore.has(productId)) WishlistStore.mergeItemIds(productId, [itemId]);
          } catch (error) {
            pushFailures += 1;
            this._report("reseed", error);
          }
        }),
      );
      return { pushFailures, unkeyable };
    })();

    this._reseeds.set(key, run);
    // Dropped again when anything failed to land, so the next read retries — and on a
    // rejection too, or the memo would hand every later caller the same dead promise.
    run.then(
      (result) => {
        if (result.pushFailures > 0) this._reseeds.delete(key);
      },
      () => this._reseeds.delete(key),
    );
    return run;
  },

  // ---- reads ----

  // One read, then adoption under the store's qualification rules. A FAILED read
  // changes nothing: offline, the wishlist still renders from the store.
  refresh({ force = false } = {}) {
    if (this._refreshPromise && !force) return this._refreshPromise;

    const run = (async () => {
      try {
        // A pending guest merge MUST land before ANY read, wherever the read came
        // from. The merge empties the guest session server-side, so a read that races
        // it resolves to that session and comes back with nothing. It cannot prune
        // (such a read is never authoritative), so the store — and the header count —
        // stay right while the grid renders empty: "Wishlist (5)" above "Your wishlist
        // is empty". Chaining this inside boot() was not enough, because the wishlist
        // page is a deferred body script and its read fires before DOMContentLoaded.
        if (WishlistIdentity.hasPendingGuestMerge()) await this.foldGuestWishlist();

        return await this._withSession(async (identity, { replaced }) => {
          // A guest whose session was replaced, or was never confirmed to hold the
          // whole list, re-seeds BEFORE reading — otherwise the read returns an empty
          // list that the shopper's items would have to survive on a technicality.
          if (identity.sessionId && (replaced || !WishlistStore.isSeeded())) {
            const { pushFailures, unkeyable } = await this.reseed(identity);
            // `seeded` licenses a read to DELETE, and an unkeyable item is precisely
            // one the server will never hold — confirming a session it is missing
            // from is what would delete it. So both counts must be zero.
            if (pushFailures === 0 && unkeyable === 0) WishlistStore.markSeeded();
          }

          const { entries, unresolved, rowCount, mayBeTruncated } = await WishlistAPI.list(identity);
          WishlistStore.adoptServer(entries, {
            authoritative: WishlistIdentity.isAuthoritative(identity, replaced),
            protectedIds: new Set(this._pending),
            mayBeTruncated,
            unresolved,
          });
          if (rowCount >= 100) console.warn("[Wishlist] list may be truncated at 100 rows");
          return entries;
        });
      } catch (error) {
        this._report("refresh", error);
        throw error;
      }
    })();

    this._refreshPromise = run;
    // Clear the slot only while it is still OURS. A forced refresh started mid-flight
    // (the wishlist page's retry button) replaces the handle, and an unconditional
    // clear in the older run's finally would null out the newer one — after which the
    // next plain refresh() starts a duplicate read instead of joining it.
    const release = () => {
      if (this._refreshPromise === run) this._refreshPromise = null;
    };
    run.then(release, release);
    return run;
  },

  // ---- login edge ----

  // Memoised for the life of the page. Both the boot sequence and the wishlist grid's
  // own read reach this on the same load, and the second merge would be aimed at a
  // session the first had already emptied and dropped. Not cleared on failure either:
  // retrying within one page gains nothing, and the next load retries anyway.
  foldGuestWishlist() {
    this._foldPromise ??= this._foldGuestWishlist();
    return this._foldPromise;
  },

  // Folds a guest session's list into the account. The session is cleared ONLY on
  // success: on failure it is still the shopper's only handle on those items, and
  // the fold is retried on the next page.
  async _foldGuestWishlist() {
    const sessionId = WishlistStore.sessionId();
    const customerId = WishlistIdentity.customerId();
    if (!sessionId || !customerId) return;

    try {
      await WishlistAPI.merge(sessionId, customerId);
    } catch (error) {
      if (!WishlistAPI.isSessionNotFound(error)) {
        this._report("merge", error);
        return;
      }
      // The COMMON case, not an edge one: a guest session lives 23h, so anyone
      // signing in a day or more after saving arrives here. There is nothing on the
      // server to merge from and the local store is the only surviving copy, so it
      // is pushed onto the account instead.
      //
      // The row ids go first — they address rows on a session that just proved gone,
      // and reseed only pushes items holding none, so leaving them would push nothing
      // and the authoritative account read would then delete the whole list.
      WishlistStore.clearAllItemIds();
      const { pushFailures } = await this.reseed({ customerId });
      // The re-seed runs BEFORE the session is dropped, never after: each push is a
      // round trip and the tab can close inside that window. Cleared first, the
      // stored state reads "signed in, no session, nothing confirmed" and the first
      // read prunes authoritatively. Re-seeded first, the same death simply replays
      // the fold on the next page.
      if (pushFailures > 0) return;
    }

    WishlistStore.clearSession();
    WishlistIdentity.reset();
  },

  // ---- boot ----

  async boot() {
    const customerId = WishlistIdentity.customerId();
    const lastCustomerId = WishlistStore.lastCustomerId();

    // Logout, detected without a hook: this browser knew a customer and the page no
    // longer renders one. The next shopper must not inherit the list.
    if (lastCustomerId && !customerId) {
      WishlistStore.reset();
      WishlistStore.clearSession();
      WishlistIdentity.reset();
      WishlistStore.setLastCustomerId(null);
      try {
        sessionStorage.removeItem(WISHLIST_RECONCILED_KEY);
      } catch {
        /* storage unavailable */
      }
      return;
    }
    WishlistStore.setLastCustomerId(customerId);

    if (WishlistIdentity.hasPendingGuestMerge()) {
      // refresh() folds first — the ordering guarantee lives there now, so it holds
      // for the wishlist page's own read too, which runs before this ever fires.
      await this.refresh().catch(() => {});
      this._markReconciled();
      return;
    }

    // Silent for a guest with neither a stored session nor a saved item: their count
    // is already 0, and asking would mint one server-side session per visitor who
    // never touches the wishlist. Efficiency, not safety — what stops a read from
    // deleting an unconfirmed guest's items is the persisted `seeded` flag.
    if (!customerId && !WishlistStore.sessionId() && WishlistStore.count() === 0) return;

    if (this._alreadyReconciled()) return;
    this.refresh()
      .then(() => this._markReconciled())
      .catch(() => {});
  },

  _alreadyReconciled() {
    try {
      return sessionStorage.getItem(WISHLIST_RECONCILED_KEY) === "1";
    } catch {
      return false;
    }
  },

  _markReconciled() {
    try {
      sessionStorage.setItem(WISHLIST_RECONCILED_KEY, "1");
    } catch {
      /* storage unavailable */
    }
  },
};

// The heart, on every surface. A Web Component rather than a one-shot binding pass
// because quick view and the search drawer re-clone their templates on every open —
// a DOMContentLoaded sweep would leave every heart opened after it dead.
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector("button");
    this.onClick = this.toggle.bind(this);
    this.button?.addEventListener("click", this.onClick);
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistChange, () => this.render());
    this.render();
  }

  disconnectedCallback() {
    this.button?.removeEventListener("click", this.onClick);
    this.unsubscribe?.();
  }

  // Null for anything that is not a product id, and the button then does nothing —
  // writing a key the API cannot resolve would create a row no other heart could match.
  get productId() {
    return WishlistAPI.productKey(this.dataset.productId);
  }

  render() {
    const isWishlisted = WishlistStore.has(this.productId);
    this.classList.toggle("is-wishlisted", isWishlisted);
    this.button?.setAttribute("aria-pressed", String(isWishlisted));
    const label = isWishlisted ? this.dataset.labelRemove : this.dataset.labelAdd;
    if (label) this.button?.setAttribute("aria-label", label);
  }

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();
    const { productId } = this;
    if (!productId) return;

    if (WishlistStore.has(productId)) {
      const itemIds = WishlistStore.remove(productId);
      WishlistSync.remove(productId, itemIds);
      return;
    }
    WishlistStore.add(productId);
    WishlistSync.add(productId);
  }
}

if (!customElements.get("wishlist-button")) {
  customElements.define("wishlist-button", WishlistButton);
}

// Re-read storage whenever this page's heap can have fallen behind it. Registered at
// module scope, not inside DOMContentLoaded: a bfcache restore fires `pageshow` and
// nothing else, so the listener has to have been installed on the original load.
//
// `persisted` is the bfcache flag. An ordinary back-navigation re-executes the scripts,
// so every heart's connectedCallback reads storage fresh and needs no help here — the
// same distinction cart-drawer.js draws.
window.addEventListener("pageshow", (event) => {
  if (event.persisted) WishlistStore.rehydrate();
});

// Fires only in the OTHER tabs, so this cannot loop back on the tab that wrote.
window.addEventListener("storage", (event) => {
  if (event.key === null || event.key.startsWith("themex-wishlist-")) WishlistStore.rehydrate();
});

document.addEventListener("DOMContentLoaded", () => {
  const counters = document.querySelectorAll("[data-wishlist-count]");

  const paintCount = () => {
    const count = WishlistStore.count();
    for (const counter of counters) {
      counter.textContent = count > 0 ? String(count) : "";
      counter.classList.toggle("is-empty", count === 0);
    }
  };

  subscribe(PUB_SUB_EVENTS.wishlistChange, paintCount);
  paintCount();

  WishlistSync.boot();
});
